/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React from 'react';
import styles from './flyout.module.scss';
import skeletonStyles from './SisterStoreFlyoutSkeleton.module.scss';
import { TrapFocus } from '@/components/Dialog/Modal/TrapFocus';
import { useLabels } from '@/hooks/useLabels';
import CloseIconWhite from '@/public/images/close-icon-white.svg';
import CloseIconDark from '@/public/images/close-dark.svg';

const placeholderImageSrc = '/part-image.png';

const labelMap = {
  checkingNearbyStores: 'label_sister_store_check_nearby',
  selectAStore: 'label_Select_A_Store',
  closeButton: 'label_Homepage_content_Close',
  productPlaceholderAlt: 'label_sister_store_product_placeholder_alt',
};

type SisterStoreFlyoutSkeletonProps = {
  handleClose: () => void;
};

const CustomSkeleton = ({
  width,
  height,
  className,
}: {
  width: string;
  height?: string;
  className?: string;
}) => (
  <div
    className={`${skeletonStyles.customSkeleton} ${className || ''}`}
    style={{ width, height }}
  />
);

const SkeletonAddressGroup = ({
  skeletonWidth,
  count = 2,
}: {
  skeletonWidth: string;
  count?: number;
}) => (
  <div className={skeletonStyles.addressGroup}>
    {Array.from({ length: count }).map((_, i) => (
      <CustomSkeleton key={i} width={skeletonWidth} />
    ))}
  </div>
);

const LoadingDots = () => (
  <div className={skeletonStyles.loadingDots}>
    <div className={skeletonStyles.dot}></div>
    <div className={skeletonStyles.dot}></div>
    <div className={skeletonStyles.dot}></div>
  </div>
);

const StoreCardSkeleton = () => (
  <div className={skeletonStyles.storeSkeletonCard}>
    <div className={skeletonStyles.storeCardTop}>
      <div className={skeletonStyles.storeAddressSection}>
        <SkeletonAddressGroup skeletonWidth="148px" />
        <SkeletonAddressGroup skeletonWidth="148px" />
      </div>
      <div className={skeletonStyles.storeDetailsSection}>
        <SkeletonAddressGroup skeletonWidth="99px" />
        <SkeletonAddressGroup skeletonWidth="99px" />
      </div>
    </div>
    <div className={skeletonStyles.storeButtonSection}>
      <div className={skeletonStyles.storeButton}>
        <LoadingDots />
      </div>
    </div>
  </div>
);

const SisterStoreFlyoutSkeleton = ({ handleClose }: SisterStoreFlyoutSkeletonProps) => {
  const labels = useLabels(labelMap);
  return (
    <TrapFocus open>
      <div
        role="dialog"
        aria-modal="true"
        className={`${styles.drawerBody} ${skeletonStyles.skeletonWrapper}`}
      >
        <div className={skeletonStyles.responsiveHeader}>
          <h2 className={skeletonStyles.responsiveHeaderTitle}>{labels.selectAStore}</h2>
          <button
            className={skeletonStyles.responsiveHeaderButton}
            onClick={handleClose}
            aria-label={labels.closeButton}
          >
            <CloseIconDark className={skeletonStyles.desktopIcon} />
            <CloseIconWhite className={skeletonStyles.mobileIcon} />
          </button>
        </div>

        {/* Product Section Skeleton */}
        <div className={skeletonStyles.productSection}>
          <div className={skeletonStyles.productContainer}>
            <div className={skeletonStyles.productImageContainer}>
              <img
                src={placeholderImageSrc}
                alt={labels.productPlaceholderAlt}
                width={64}
                height={64}
              />
            </div>
            <div className={skeletonStyles.productInfo}>
              <div className={skeletonStyles.productTitleLine}>
                <CustomSkeleton width="247px" />
              </div>
              <div className={skeletonStyles.productSubHeading}>
                <CustomSkeleton width="247px" />
              </div>
            </div>
          </div>

          {/* Price Section Skeleton */}
          <div className={skeletonStyles.priceSection}>
            <div className={skeletonStyles.priceAmount}>
              <CustomSkeleton width="57px" />
            </div>
          </div>
        </div>

        <div className={skeletonStyles.checkingStoresLabel}>{labels.checkingNearbyStores}</div>

        {/* Store List Skeleton */}
        <div className={skeletonStyles.storeListSection}>
          {Array.from({ length: 3 }).map((_, index) => (
            <StoreCardSkeleton key={index} />
          ))}
        </div>
      </div>
    </TrapFocus>
  );
};

export default SisterStoreFlyoutSkeleton;
